import { query } from '@trlogic/client-js';
import MessageUtil from '../util/MessageUtil';
import { EnrichedComponentCategory } from '../domain/category/CategoryOperationDomains';
import _ from 'lodash';
import ComponentName from '../constant/ComponentName';
import GatewayContainer from '../config/GatewayContainer';
import FolderClient from '../client/folder/FolderClient';
import GatewayNames from '../constant/GatewayNames';
import { dispatch } from '../redux/store';
import { openBulkActionDialogFromDependency } from '../redux/actions/BulkActionActions';
import { CraType } from '../constant/Constants';
import Query = query.Query;
import LanguageUtil from '../util/LanguageUtil';
import DeployErrorUtil from '../util/DeployErrorUtil';
import { CraClient } from '../client/cra/CraClient';
import {
  CraSegmentType,
  CraDateSegmentsSchema,
  CraNumberSegmentsSchema,
  CraStringSegmentsSchema,
  CraVariableSchema,
  PolicyJsonModel,
} from '../domain/cra/CraDomains';
import { changePolicyDirty } from '../redux/actions/PolicyActions';
import ExpressionBridge from '../util/ExpressionBridge';
import { Syntax } from '@trlogic/ui-pack';

class CraService {
  private readonly expressionBridge: ExpressionBridge;

  constructor(expressionBridge: ExpressionBridge) {
    this.expressionBridge = expressionBridge;
  }

  private readonly craClient: CraClient = GatewayContainer.getInstance().get(GatewayNames.CRA_CLIENT);
  private folderClient: FolderClient = GatewayContainer.getInstance().get(GatewayNames.FOLDER_CLIENT);

  //TODO fix LanguageUtil
  public async save(_policy: PolicyJsonModel, isUpdate: boolean, folder: EnrichedComponentCategory): Promise<boolean> {
    try {
      const policy: PolicyJsonModel = _.cloneDeep(_policy);
      if (policy.variables && policy.variables.length > 0) {
        for (const variable of policy.variables) {
          if (variable.type === 'API' && variable.mapping) {
            try {
              variable.mapping = await this.parseExpression(variable.mapping);
            } catch (e) {
              console.error('Error processing mapping in save:', e);
            }
          }
        }
      }

      if (policy.assessor == undefined) {
        MessageUtil.error({
          summary: LanguageUtil.get({ id: 'common.label.cra', defaultMessage: 'CRA' }),
          detail: LanguageUtil.get({ id: 'messages.error.withoutName', defaultMessage: 'You can not save without type' }),
        });
        return false;
      }
      const isExist = await this.craClient.isExist(policy.assessor);
      if (isExist && !isUpdate) {
        MessageUtil.alreadyExist(LanguageUtil.get({ id: 'messages.warning.craAlready', defaultMessage: 'CRA already exist' }));
        return false;
      }
      if (!isUpdate && folder) {
        await this.folderClient.addItemToCategory(ComponentName.POLICY, folder.id, policy.assessor);
      }
      await this.craClient.save(policy);
      MessageUtil.success({
        summary: LanguageUtil.get({ id: 'common.label.cra', defaultMessage: 'CRA' }),
        detail: LanguageUtil.get({ id: 'messages.success.craSaved', defaultMessage: 'CRA saved successfully!' }),
      });
      dispatch(changePolicyDirty(false));
      return true;
    } catch (e) {
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'common.label.cra', defaultMessage: 'CRA' }),
        detail: LanguageUtil.get({ id: 'messages.error.craSaved', defaultMessage: 'CRA save failed!' }),
      });
      return false;
    }
  }

  public async delete(id: string, assessor: CraType): Promise<boolean> {
    try {
      const response = await this.craClient.delete(id, assessor);
      if (response.code) {
        return false;
      }
      MessageUtil.success({ content: LanguageUtil.get({ id: 'messages.success.craDeleted', defaultMessage: 'CRA removed successfully.' }) });
      return true;
    } catch (e: any) {
      if (DeployErrorUtil.show(e, LanguageUtil.get({ id: 'common.label.cra', defaultMessage: 'CRA' }))) {
        return false;
      }
      const dependencies: any = e.response.data;
      if (dependencies?.message == 'Dependency Exception') {
        dispatch(openBulkActionDialogFromDependency(dependencies, ComponentName.POLICY));
        return false;
      }
      MessageUtil.error({ content: LanguageUtil.get({ id: 'messages.error.craDeleted', defaultMessage: 'CRA remove failed!' }) });
      return false;
    }
  }

  public async deploy(policy: PolicyJsonModel): Promise<boolean> {
    try {
      await this.craClient.deploy(policy.assessor);
      MessageUtil.success({
        summary: LanguageUtil.get({ id: 'common.label.cra', defaultMessage: 'CRA' }),
        detail: LanguageUtil.get({ id: 'messages.success.craDeployed', defaultMessage: 'CRA deployed successfully!' }),
      });
      return true;
    } catch (e) {
      if (DeployErrorUtil.show(e, LanguageUtil.get({ id: 'common.label.cra', defaultMessage: 'CRA' }))) {
        return false;
      }
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'common.label.cra', defaultMessage: 'CRA' }),
        detail: LanguageUtil.get({ id: 'messages.error.craDeployed', defaultMessage: 'CRA deployment failed!' }),
      });
      return false;
    }
  }

  public async undeploy(id: string): Promise<boolean> {
    try {
      await this.craClient.undeploy(id);
      MessageUtil.success({
        summary: LanguageUtil.get({ id: 'common.label.cra', defaultMessage: 'CRA' }),

        detail: LanguageUtil.get({ id: 'messages.success.craUndeployed', defaultMessage: 'CRA undeployed successfully!' }),
      });
      return true;
    } catch (e: any) {
      if (DeployErrorUtil.show(e, LanguageUtil.get({ id: 'common.label.cra', defaultMessage: 'CRA' }))) {
        return false;
      }
      const dependencies: any = e.response.data;
      if (dependencies?.message == 'Dependency Exception') {
        dispatch(openBulkActionDialogFromDependency(dependencies, ComponentName.POLICY));
        return false;
      }
      MessageUtil.error({ content: LanguageUtil.get({ id: 'messages.error.craUndeployed', defaultMessage: 'Cra undeploy failed!' }) });
      return false;
    }
  }

  public async getCount(query: Query): Promise<number> {
    return await this.craClient.getCount(query);
  }

  public async isExist(id: string): Promise<boolean> {
    return await this.craClient.isExist(id);
  }

  public async variable(object: CraVariableSchema, assessor: CraType, isUpdate?: boolean): Promise<boolean> {
    try {
      if (!isUpdate) {
        const isExist = await this.craClient.existVariable(object.name, assessor);
        if (isExist) {
          MessageUtil.alreadyExist(LanguageUtil.get({ id: 'messages.warning.variableAlready', defaultMessage: 'Variable already exists' }));
          return false;
        }
      }

      const payload = {
        ...object,
        assessor,
      };

      await this.craClient.variable(payload);
      MessageUtil.success({
        summary: LanguageUtil.get({ id: 'common.label.cra.variable', defaultMessage: 'CRA Variable' }),
        detail: isUpdate
          ? LanguageUtil.get({ id: 'messages.success.variableUpdated', defaultMessage: 'Variable updated successfully!' })
          : LanguageUtil.get({ id: 'messages.success.craSaved', defaultMessage: 'Variable saved successfully!' }),
      });
      return true;
    } catch (e) {
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'common.label.cra.variable', defaultMessage: 'CRA Variable' }),
        detail: isUpdate
          ? LanguageUtil.get({ id: 'messages.error.variableUpdate', defaultMessage: 'Variable update failed!' })
          : LanguageUtil.get({ id: 'messages.error.craSaved', defaultMessage: 'Variable save failed!' }),
      });
      return false;
    }
  }

  public async variableEnable(id: string, assessor: CraType): Promise<boolean> {
    try {
      const response = await this.craClient.variableEnable(id, assessor);
      if (response.code) {
        return false;
      }
      MessageUtil.success({ content: LanguageUtil.get({ id: 'messages.success.enabled', defaultMessage: 'Variable enable change successfully' }) });
      return true;
    } catch (e: any) {
      MessageUtil.error({
        content: LanguageUtil.get({
          id: 'messages.error.enabled',
          defaultMessage: 'Error while change enable variable.',
        }),
      });
      return false;
    }
  }

  public async segment(object: CraStringSegmentsSchema | CraNumberSegmentsSchema | CraDateSegmentsSchema, type: CraSegmentType, showMessage?: boolean): Promise<boolean> {
    try {
      await this.craClient.segment(object, type);
      showMessage != false && MessageUtil.success({
        summary: LanguageUtil.get({ id: 'common.label.cra.segment', defaultMessage: 'CRA Segment' }),
        detail: LanguageUtil.get({ id: 'messages.success.craSaved', defaultMessage: 'Segment created successfully' }),
      });
      return true;
    } catch (e) {
      showMessage != false && MessageUtil.error({
        summary: LanguageUtil.get({ id: 'pages.cra.segment', defaultMessage: 'CRA Segment' }),
        detail: LanguageUtil.get({ id: 'messages.error.craSaved', defaultMessage: 'Segment creation failed' }),
      });
      return false;
    }
  }

  public async deleteSegment(id: string, assessor: CraSegmentType, showMessage?: boolean): Promise<boolean> {
    try {
      await this.craClient.deleteSegment(id, assessor);
      showMessage != false && MessageUtil.success({
        summary: LanguageUtil.get({ id: 'common.label.cra.segment', defaultMessage: 'CRA Segment' }),
        detail: LanguageUtil.get({ id: 'messages.success.deletedStore', defaultMessage: 'Segment deleted successfully' }),
      });
      return true;
    } catch (e) {
      showMessage != false && MessageUtil.error({
        summary: LanguageUtil.get({ id: 'pages.cra.segment', defaultMessage: 'CRA Segment' }),
        detail: LanguageUtil.get({ id: 'messages.error.deletedStore', defaultMessage: 'Segment deletion failed' }),
      });
      return false;
    }
  }

  public async getSegmentByVariableName(variableName: string, assessor: CraSegmentType): Promise<any> {
    try {
      return await this.craClient.getSegmentByVariableName(variableName, assessor);
    } catch (e) {
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'pages.cra.segment', defaultMessage: 'CRA Segment' }),
        detail: LanguageUtil.get({ id: 'messages.error.getSegment', defaultMessage: 'Get segment failed' }),
      });
      return null;
    }
  }

  public async getAssessmentResults(assessor: CraType, query: Query): Promise<any[]> {
    try {
      return await this.craClient.getAssessmentResults(assessor, query);
    } catch (e) {
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'common.label.assessment', defaultMessage: 'CRA Assessment' }),
        detail: LanguageUtil.get({ id: 'messages.error.getAssessmentResults', defaultMessage: 'Get assessment results failed' }),
      });
      return [];
    }
  }

  public async getAssessmentResultById(assessor: CraType, id: string): Promise<any | undefined> {
    try {
      return await this.craClient.getAssessmentResultById(assessor, id);
    } catch (e) {
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'common.label.assessment', defaultMessage: 'CRA Assessment' }),
        detail: LanguageUtil.get({ id: 'messages.error.getAssessmentResults', defaultMessage: 'Get assessment results failed' }),
      });
      return undefined;
    }
  }

  public async countAssessmentResults(assessor: CraType, query: Query): Promise<number | undefined> {
    try {
      return await this.craClient.countAssessmentResults(assessor, query);
    } catch (e) {
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'common.label.assessment', defaultMessage: 'CRA Assessment' }),
        detail: LanguageUtil.get({ id: 'messages.error.countAssessmentResults', defaultMessage: 'Get assessment result count failed' }),
      });
      return undefined;
    }
  }

  private async parseExpression(syntax: Syntax) {
    const iterateSyntax = (syntax: Syntax): Syntax => {
      return {
        id: syntax.id,
        value: syntax.value,
        children: syntax.children?.map((c) => iterateSyntax(c)),
      };
    };
    return iterateSyntax(await this.expressionBridge.stop(syntax));
  }
}

export default CraService;
